import { useMonyawn } from "../context/MonyawnContext";
import { createDM, createProof, createCheckpoint } from "../workflow";
import { extractSignalsFromText } from "../intelligence";
import { DMStatus } from "../types";
import { DMDraft } from "../context/MonyawnContext.types";

export function useDMOps() {
  const { patchState, selectedOpportunity, setNotice } = useMonyawn();

  const handleDMSubmit = (draft: DMDraft) => {
    if (!selectedOpportunity) {
      setNotice({ tone: "info", message: "Select a play before drafting DMs." });
      return;
    }

    const dm = createDM(
      selectedOpportunity.opportunity_id,
      draft.channel,
      draft.subject,
      draft.body,
      extractSignalsFromText(`${draft.subject}\n${draft.body}`, selectedOpportunity.company_name),
    );

    const proof = createProof({
      opportunity_id: selectedOpportunity.opportunity_id,
      proof_type: "message",
      source_label: dm.subject || "Untitled DM draft",
      origin: "system_generated",
      review_status: "unreviewed",
      parse_status: "not_applicable",
      evidence_note: "DM operations",
      content_summary: dm.body,
      extracted_signals: dm.extracted_signals,
    });

    const checkpoint = createCheckpoint(
      selectedOpportunity,
      "DM signal extraction",
      "DM text was parsed for contact, timing, and interview signals.",
      dm.extracted_signals &&
        (dm.extracted_signals.emails.length ||
          dm.extracted_signals.dates.length ||
          dm.extracted_signals.times.length)
        ? "high"
        : "medium",
      "proceed_with_warning",
      `Captured ${dm.extracted_signals?.emails.length ?? 0} emails, ${dm.extracted_signals?.dates.length ?? 0} dates, and ${dm.extracted_signals?.times.length ?? 0} time references from DMs.`,
      "low",
      "low",
    );

    patchState(
      (current) => ({
        ...current,
        dms: [dm, ...current.dms],
        proof: [proof, ...current.proof],
        checkpoints: [checkpoint, ...current.checkpoints],
      }),
      "DM draft created with structured signal extraction and attached to the proof record.",
    );

    return dm;
  };

  const setDMStatus = (dmId: string, status: DMStatus) => {
    patchState(
      (current) => ({
        ...current,
        dms: current.dms.map((c) =>
          c.dm_id === dmId ? { ...c, status, updated_at: new Date().toISOString() } : c,
        ),
      }),
      "DM status updated.",
    );
  };

  return {
    handleDMSubmit,
    setDMStatus,
  };
}
